import Light, { VibiaLightParams } from "../composition/Light";
import Article from "./Article"
import Variation from "./Variation"
import Price from "./Price"
import { modelAttribute, variationType } from "./catalog";
import { VariationProps } from "./variation";

const variationTypes: variationType[] = Variation.getTypes()
const attributes: variationType[] = ['color', 'temperature', 'control', 'installation']
const technicalAttributes: modelAttribute[] = ['material', 'cri']

const getColorData = (variation: Variation) => ({
	label: variation.getLabel(true),
	description: variation.getDescriptions(),
	buttonColor: variation.getProps().value,
	ral: variation.getProps().ral.description.en
})

const uniqueArray = (array) => {
	const set = new Set()
	array.forEach(elem => set.add(JSON.stringify(elem)))
	array.forEach(elem => {
		const stringifiedElem = JSON.stringify(elem)
		if (set.has(stringifiedElem)) {
			set.delete(stringifiedElem)
			set.add(elem)
		}

	})
	return Array.from(set)
}

export const getModifier = (string: any) => typeof string == 'string'
	? string?.split(' ')[0]?.trim()
	: string

export const getBov = (type: "default" | "optional" | "variation") => {
	const dictionary = {
		"default": "B",
		"optional": "O",
		"variation": "V",
	}
	return dictionary[type]
}

export default class ArticleCollection {
	props: Variation[]; // Should be named variations
	baseArticles?: Article[];
	basePrices?: Price[]

	constructor(variations: Variation[], baseArticles?: Article[], basePrices?: Price[]) {
		this.props = variations
		if (baseArticles) {
			for (const baseArticle of baseArticles) {
				const model = baseArticle.getModel()
				const getShownAttributes = this.getShownAttributes(model)
				baseArticle.setHasAttributes(getShownAttributes)
			}
			this.baseArticles = baseArticles
		}
		if (basePrices) {
			this.basePrices = basePrices
		}
	}

	getBaseArticles(): Article[] {
		return this.baseArticles
	}

	getActiveBaseArticles(): Article[] {
		return this.getBaseArticles().filter((article) => !!article.getProps().status.isActive && !!article.getProps().status.isCatalog)
	}

	getBaseArticle(model: string): Article {
		return this.baseArticles?.find(article => article.getProps().model == model)
	}

	getBasePrice(model: string, rate: number): Price {
		return this.basePrices?.find(price => price.getModel() == model && price.getRate() == rate)
	}

	// Returns ALL available variations for this model.
	getModelVariations(model: string): Variation[] {
		const requiredAttributes = this.getRequiredAttributes(model)
		let result = []
		for (const attribute of requiredAttributes) {
			result = [...result, ...this.getVariationsByType(model, attribute)]
		}
		return result
	}

	getVariationsByType(model: string, type: variationType) {
		const variations = model ?
			(this.props?.filter ? this.props.filter(v => v.getProps().model == model) : []) :
			this.props
		const typeVariations = variations.filter(v => v.getType() == type).filter(v => v.isBase() || v.isOptional() || v.isVariation())

		// Add variation from CRM if no base variation found in ERP
		if (!typeVariations.find(v => v.isBase())) {
			const baseArticle = this.getBaseArticle(model)
			const crmVariation = Variation.fromArticle(baseArticle, type)
			if (crmVariation) {
				typeVariations.push(crmVariation)
			}
		}
		return typeVariations
	}

	// Returns applied variations given a reference
	// TODO: Add base installation variation if it exists & not present in reference
	// Wasn't because it has no effects in price or reference, but it is missing from this result.
	getReferenceVariations(reference: String): Variation[] {
		const model = reference.slice(0, 4);
		const referenceWithoutDash = reference.replace('-', '');
		const codes = {
			color: referenceWithoutDash.slice(4, 6),
			temperature: referenceWithoutDash.slice(7, 8),
			control: referenceWithoutDash.slice(8, 9),
			installation: referenceWithoutDash.slice(9, 12)

		}
		const appliedVariations = []
		const modelVariations = this.getModelVariations(model);

		for (const variation of modelVariations) {
			if (variation.getModifier() == codes[variation.getType()]) {
				appliedVariations.push(variation)
			}
		}

		return appliedVariations
	}

	// Returns applied variations given an Article instance
	getArticleVariations(article: Article, priceReference?: boolean): Variation[] {
		const reference = article.getReference(priceReference)
		const model = reference.slice(0, 4);
		const variations = this.getReferenceVariations(reference)
		const pendantHeightVariation = this.getVariationsByType(model, 'pendantHeight')[0]
		const pendantHeightVariationValue = pendantHeightVariation?.getValue()
		const articlePendantHeight = article.getAttribute('pendantHeight')
		if (
			articlePendantHeight &&
			pendantHeightVariation &&
			pendantHeightVariationValue.standard != article.getAttribute('pendantHeight')
		) {
			variations.push(pendantHeightVariation.getSpecificPendantHeightVariation(articlePendantHeight))
		}
		return variations
	}

	//Returns an article instance given a reference
	getArticle(reference: string): Article {
		const appliedVariations = this.getReferenceVariations(reference)
		const model = reference?.slice(0, 4);

		let article = this.getBaseArticle(model);
		if (!article) return null
		for (const variation of appliedVariations) {
			article = variation.getArticle(article)
		}
		return article
	}

	getReferencePrice(reference: string, rate: number): Price {
		const model = reference.slice(0, 4);
		const variations = this.getReferenceVariations(reference)
		let price = this.getBasePrice(model, rate)
		for (const variation of variations) {
			price = variation.getPrice(price)
		}
		return price
	}

	getArticlePrice(article: Article, rate: number): Price {
		const model = article.getModel()
		const variations = this.getArticleVariations(article, true)
		let price = this.getBasePrice(model, rate)
		for (const variation of variations) {
			price = variation.getPrice(price)
		}
		return price
	}

	getRequiredAttributes(model: string): variationType[] {
		const attributes = [...variationTypes]
		return attributes.filter(attribute => this.hasAnyVariation(model, attribute))
	}

	getPersonalizableAttributes(model: string): variationType[] {
		const attributes = this.getRequiredAttributes(model)
		return attributes.filter(attribute => attribute == 'pendantHeight'
			? this.isPendantHeightVariable(model)
			: this.hasMultipleVariations(model, attribute)
		)
	}

	getNonPersonalizableAttributes(model: string): variationType[] {
		const attributes = this.getRequiredAttributes(model)
		const personalizable = this.getPersonalizableAttributes(model)
		return attributes.filter(attr => !personalizable.includes(attr))
	}

	getShownAttributes(model: string): variationType[] {
		return this.getRequiredAttributes(model)
	}

	hasMultipleVariations(model: string, type: variationType): boolean {
		return this.getVariationsByType(model, type).length > 1
	}

	hasAnyVariation(model: string, type: variationType): boolean {
		return this.getVariationsByType(model, type).length > 0
	}

	isPendantHeightVariable(model: string): boolean {
		const variation = this.getVariationsByType(model, 'pendantHeight')[0]
		if (!variation) return false
		const constraint = variation.getValue()
		if (!constraint || !constraint.max || !constraint.min) return false
		return constraint?.max != constraint?.min
	}

	getTechnicalInfo(): any { // TODO: Filter by subFamily
		let colorMOQ = 0
		const result: any = {}
		const fields = [...attributes, ...technicalAttributes, "baseColor"]
		fields.forEach((field) => result[field] = new Set())

		const baseArticles: Article[] = this.getBaseArticles()

		attributes.forEach((attribute) => {
			const variations = this.getVariationsByType(null, attribute)
			if (attribute == 'color') {
				const baseColors = variations.filter(variation => variation.getBOV() == "B" || variation.getBOV() == "V")
				const extraColors = variations.filter((variation) => variation.getBOV() == "O");
				[...baseColors, ...extraColors].forEach(v => {
					if (v.getMOQ() > colorMOQ)
						colorMOQ = v.getMOQ()
				})
				result['color'] = uniqueArray(extraColors.map(variation => getColorData(variation)))
				result['baseColor'] = uniqueArray(baseColors.map(variation => getColorData(variation)))
				return
			}

			result[attribute] = uniqueArray(
				variations.filter(variation => variation.getType() == attribute)
					.map(attributeVariation => ({ description: attributeVariation.getDescriptions() }))
			)
		})

		technicalAttributes.forEach((technicalAttribute) => {

			if (technicalAttribute == 'material') {
				result[technicalAttribute] = uniqueArray(baseArticles.map(baseArticle => baseArticle.getMaterialsDescriptions()).flat()).map(object => ({ description: object }))
			}

			if (technicalAttribute == 'cri') {
				result[technicalAttribute] = uniqueArray(baseArticles.map(baseArticle => baseArticle.getCRIDescriptions())).map(object => ({ description: object }))
			}

			// result[technicalAttribute] = uniqueArray(
			// 	baseArticles.map(baseArticle => {
			// 		Array.isArray(baseArticle.getAttribute(technicalAttribute))
			// 			? baseArticle.getAttribute(technicalAttribute, false).forEach(attr => result[technicalAttribute].add(attr))
			// 			: result[technicalAttribute].add(baseArticle.getAttribute(technicalAttribute, false))
			// 	})
			// )
		})

		fields.forEach(field => result[field] = Array.from(result[field]))
		result.colorMOQ = colorMOQ
		return result
	}

	getDefaultPendantHeight(model): number {
		const variations = this.getVariationsByType(model, 'pendantHeight')
		const variation = variations[0]
		return variation ? variation.getValue()?.standard : null
	}

	isPendantHeightValid(model, pendantHeight): boolean {
		const pendantRestrictions = this.getVariationsByType(model, 'pendantHeight')[0]?.getValue()
		if (!pendantRestrictions) return false
		const safeRestrictions = {
			...pendantRestrictions,
			min: Math.min(pendantRestrictions.min, pendantRestrictions.standard),
			max: Math.max(pendantRestrictions.max, pendantRestrictions.standard)
		}
		return pendantHeight >= safeRestrictions.min && pendantHeight <= safeRestrictions.max
	}

	// Decides whether or not an article should be allocated a special code (articleCode)
	articleNeedsLWCode(article: Article) {
		const model = article.getModel()
		const pendantHeight = article.getAttribute('pendantHeight')
		return this.isPendantHeightValid(model, pendantHeight) &&
			pendantHeight != this.getDefaultPendantHeight(model)
	}

	// TODO: Move to Table
	getLightVariations(element: Light): Variation[] {
		const model = element.getModel()
		const appliedVariations = []
		const requiredAttributes = this.getRequiredAttributes(model)

		for (const attribute of requiredAttributes) {
			const attributeValue = element.getAttribute(attribute, true)
			if (!attributeValue) continue // Attribute doesn't have value -> No variation applied for this attribute
			const attributeModifier = element.getAttributeModifier(attribute)
			const attributeVariations = this.getVariationsByType(model, attribute)
			if (attribute == 'pendantHeight') {
				const genericVariation = attributeVariations[0]
				if (genericVariation && attributeValue) {
					const pendantHeightVariation = genericVariation.getSpecificPendantHeightVariation(attributeValue)
					appliedVariations.push(pendantHeightVariation)
				}
			} else {
				const appliedVariation = attributeVariations.find(v => v.getModifier(true) == attributeModifier)
				if (appliedVariation) appliedVariations.push(appliedVariation)
			}
		}

		return appliedVariations
	}

	hasLightAllVariations(element: Light) {
		const model = element.getModel()
		const requiredAttributes = this.getRequiredAttributes(model)

		for (const attribute of requiredAttributes) {
			const attributeModifier = element.getAttributeModifier(attribute)
			const attributeVariations = this.getVariationsByType(model, attribute)
			const validAttributeVariations = attributeVariations.filter(el => el.isAvailable())
			if (attribute != 'pendantHeight') {
				const variationFound = validAttributeVariations.find((variation: Variation) => {
					return attributeModifier == variation.getModifier(true)
				})
				if (!variationFound) {
					return false
				}
			}
		}
		return true
	}



	// TODO: Move to Table
	getLightMissingAttributes(element: Light): variationType[] {
		const missingAttributes = []
		const model = element.getModel()
		const requiredAttributes = this.getRequiredAttributes(model)

		for (const attribute of requiredAttributes) {
			const attributeValue = element.getAttribute(attribute, true)
			if (!attributeValue)
				missingAttributes.push(attribute)
		}

		return missingAttributes
	}

	// TODO: Move to Table
	getLightInvalidAttributes(element: Light): variationType[] {
		const model = element.getModel()
		const requiredAttributes = this.getRequiredAttributes(model)
		const notValidAttributes = Variation.getTypes().filter(attr => !requiredAttributes.includes(attr)) // Attributes that have no variation
		const notValidAttributesWithValues = notValidAttributes.filter(attr => element.getAttribute(attr))

		let validPendantHeight = false
		if (requiredAttributes.includes('pendantHeight')) {
			const pendantHeight = element.getAttribute('pendantHeight')
			validPendantHeight = this.isPendantHeightValid(model, pendantHeight)
		}

		const appliedVariations = this.getLightVariations(element)
		const missingAttributes = this.getLightMissingAttributes(element)
		const attributesWithValidValues = [
			...appliedVariations.map(v => v.getType()),
			...missingAttributes, // Unset is a valid value
			// if pendantHeight is standard, no variation will be applied. Add it to valid if its value is valid
			...validPendantHeight ? ['pendantHeight'] : []
		]
		const attributesWithInvalidValues = [
			...requiredAttributes.filter(attr => !attributesWithValidValues.includes(attr)),
			...notValidAttributesWithValues
		]

		return attributesWithInvalidValues
	}

	// TODO: Move to Table
	getLightArticle(element: Light) {
		const model = element.getCode()
		const baseArticle = this.getBaseArticle(model)

		if (!baseArticle) {
			throw (`No base article exists for model ${model}`)
		}

		const appliedVariations = this.getLightVariations(element)
		const missingAttributes = this.getLightMissingAttributes(element)

		const attributesWithInvalidValues = this.getLightInvalidAttributes(element)
		if (attributesWithInvalidValues.length) {
			const invalidValues = {}
			attributesWithInvalidValues.forEach(attr => invalidValues[attr] = element.getAttribute(attr, true))
			throw ({
				message: `Tried to get Article for Light (model=${model}) with invalid attribute values`,
				invalidValues
			})
		}

		// Apply the variations to the base article
		let article = baseArticle
		article.props.id = element.getId()
		article.props.grandparentId = element.getParent()?.getParent()?.getId()
		article.props.parentId = element.getParent()?.getId()
		article.props.type = element.getClassName()
		article.props.elementOrderValue = element.getElementOrderValue()
		const applicationLocationIdSelected = element.getApplicationLocationId()
		if (applicationLocationIdSelected) {
			const applicationsLocations = article.props.applicationsLocations.filter(app => app.id == applicationLocationIdSelected)
			if (applicationsLocations.length === 1) {
				article.props.applicationsLocations = applicationsLocations
			} else {
				console.error(`Application Location ${applicationLocationIdSelected} doesn't exist on this article`)
			}
		}
		for (const variation of appliedVariations) {
			article = variation.getArticle(article)
		}

		for (const missingAttribute of missingAttributes) {
			article.deleteAttribute(missingAttribute)
		}

		// Variation.getArticle now adds the resulting height to the article, rendering article.setPendantHeight useless in this case
		// const pendantHeight = element.getAttribute('pendantHeight')
		// if (pendantHeight) {
		// 	article.setPendantHeight(pendantHeight)
		// }

		// const lightColorVariation = element.getAttributeVariation('color')
		// if (lightColorVariation?.description) {
		// 	const modifier = lightColorVariation.modifier
		// 	const description = lightColorVariation.description
		// 	Object.entries(description).map(([lang, desc]: [string, string]) => description[lang] = desc.replace(modifier, '').trim())
		// 	article.setAttributeDescription('color', description)
		// }

		return article
	}

	// TODO -> Dejar un solo contrato (Que no haya posibilidad de pasar Array o Object)
	// Separar en 2 -> 3 arrays un metodo y 1 objeto otro metodo
	static getInstance(variationsData: any[] | Object, baseArticlesData?: any[], pricesData?: any[]): ArticleCollection {
		if (!Array.isArray(variationsData)) {
			const variations = variationsData['variations']
			const base = variationsData['base']
			const prices = variationsData['prices']
			
			return new ArticleCollection(
				variations.map(variationData => new Variation(variationData)),
				base?.map(baseArticleData => new Article(baseArticleData)),
				prices?.map(priceData => new Price(priceData))
			)
		} else if (Array.isArray(variationsData)) {
			return new ArticleCollection(
				variationsData.map(variationData => new Variation(variationData)),
				baseArticlesData?.map(baseArticleData => new Article(baseArticleData)),
				pricesData?.map(priceData => new Price(priceData))
			)
		}
	}

}
